import { reduceArrayOfObjToObj } from "@utils/array-helpers";
import { userErrorMessage } from "@utils/custom-errors";
import { getJWT } from "@utils/browser-helpers";
// adapters
import consoleConfigAdapter from "@adapters/console-config";
import promotionsAdapter from "@features/promotions/adapters";
import generalEndPointsAdapter from "@adapters/general-endpoints";
import selectClientsAdapter from "@features/select-clients/adapters";
import c3Adapter from "@features/c3/adapters";

import {
  setLocationGroups,
  setStores,
  setDeviceSegments,
  setConsoleConfigs,
  setPromotionOptions,
  clearGeoSegmentForm,
  setToastMessage,
  setFileOptions,
  setCTIDOptions,
  setCommercialCTIDOptions,
  reportError,
  setFeaturePermissions,
  setIsCommercialClient,
} from "@actions/global";
import { getStore } from "@store";
import {
  getDevSessionOverrides,
  logDevOverride,
} from "@/shared/dev/dev-role-override";
//utils
import { getFileManagerAdapter } from "@features/files/file-interface";

// Read ctid at call-time rather than module-load time so it reflects
// the current value after client selection.
const getCTID = () => localStorage.getItem("ctid");

export const getLocalization = async (store) => {
  const CTID = getCTID();
  if (CTID && [222, 221].indexOf(parseInt(CTID, 10)) > -1) {
    store.dispatch(setStores({ total: 0, stores: [] }));
    store.dispatch(setLocationGroups([]));
    return;
  }
  let stores = await generalEndPointsAdapter.fetchAllStores("/live");
  let locationGroups = await generalEndPointsAdapter.fetchLocationGroups(
    "/live"
  );
  stores = stores && stores.filter((store) => store.store_type !== "online");
  stores = reduceArrayOfObjToObj(stores, "store_id");
  let activeLocationGroups = [];
  if (locationGroups && stores) {
    locationGroups.forEach((location, ind, obj) => {
      let temp = [];
      //handle case where locationGroup has a store id that does not exist ignore store
      location.location_ids.forEach((location_id) => {
        if (stores[location_id]) {
          temp.push(stores[location_id]);
        }
      }); //handle non
      location.stores = temp;
      //handle case where locationGroup has no stores, saves active values to new arr
      if (location.stores.length !== 0 && location.location_ids.length !== 0) {
        activeLocationGroups.push(location);
      }
    });
  }
  activeLocationGroups = reduceArrayOfObjToObj(
    activeLocationGroups,
    "location_group_id"
  );

  store.dispatch(setLocationGroups(activeLocationGroups));
  store.dispatch(setStores(stores));
};

export const getUserSegments = async (store) => {
  let segments = await generalEndPointsAdapter.fetchDeviceSegments();
  segments.map((segment) =>
    (({ name, segment_id, description, device_count, date_modified }) => ({
      name,
      segment_id,
      description,
      device_count,
      date_modified,
    }))(segment)
  );
  store.dispatch(setDeviceSegments(segments));
};

export const getConfigurationDetails = async (store) => {
  const ctid = getJWT().ctid;
  const storedCtid = localStorage.getItem("ctid");

  // Guard: Do not attempt to fetch console_config without a valid ctid.
  // Without a ctid the API URL becomes malformed (/api/2500//console_config)
  // which causes the backend to reject the request or redirect to /logout?error=NO_CRED.
  if (!ctid && !storedCtid) {
    console.warn("getConfigurationDetails: No ctid available — skipping console_config fetch.");
    return;
  }

  let isCommercialClient = false;
  if (ctid) {
    let commercialClients =
      await selectClientsAdapter.fetchCommercialClientCTIDs();

    isCommercialClient = commercialClients.clients
      .map((commercialClient) => commercialClient.clienttype_id)
      .includes(ctid);
  }
  let configs = await consoleConfigAdapter.fetchConsoleConfig(
    isCommercialClient
  );

  if (!configs) {
    console.warn("getConfigurationDetails: fetchConsoleConfig returned no data.");
    return;
  }

  configs = {
    ...configs,
    pureResponse: {
      ...configs.pureResponse,
      feature_permissions: {
        ...(configs.pureResponse || {}).feature_permissions,
      },
    },
  };

  // Local-dev-only: allow overriding user_level / feature_permissions via
  // VITE_DEV_USER_LEVEL, VITE_DEV_FEATURE_PERMISSIONS, or window.__DEV_SESSION__.
  // In prod builds this branch is DCE'd.
  const devSession = getDevSessionOverrides();
  if (devSession) {
    const before = {
      user_level: configs.pureResponse.user_level,
      feature_permissions: configs.pureResponse.feature_permissions,
    };
    configs.pureResponse = {
      ...configs.pureResponse,
      ...(devSession.user_level ? { user_level: devSession.user_level } : {}),
      ...(devSession.feature_permissions
        ? {
            feature_permissions: {
              ...configs.pureResponse.feature_permissions,
              ...devSession.feature_permissions,
            },
          }
        : {}),
    };
    logDevOverride("sessionDetails", before, {
      user_level: configs.pureResponse.user_level,
      feature_permissions: configs.pureResponse.feature_permissions,
    });
  }

  store.dispatch(setConsoleConfigs(configs));
  store.dispatch(setIsCommercialClient(isCommercialClient));
};
export const getClientFeaturePermissions = async (store) => {
  const { client } = await c3Adapter.clientManagement.fetchIndividualClient(
    getCTID()
  );
  store.dispatch(setFeaturePermissions(client.feature_permissions));
};
export const getPromotionOptions = async (store) => {
  const query = {
    status: "live",
  };
  const { promotions } = await promotionsAdapter.fetchPromotions(query);
  store.dispatch(setPromotionOptions(promotions));
};

export const saveGeoSegment = async (geoSegmentDetails, store = getStore()) => {
  let result = await generalEndPointsAdapter.createGeoSegment(
    geoSegmentDetails
  );
  if (result.segment) {
    let segment = (({
      name,
      segment_id,
      description,
      device_count,
      date_modified,
    }) => ({ name, segment_id, description, device_count, date_modified }))(
      result.segment
    );
    store.dispatch(clearGeoSegmentForm());
    store.dispatch(setDeviceSegments([segment]));
    store.dispatch(
      setToastMessage(`Geo Segment '${segment.name}' has been saved`)
    );
  } else {
    store.dispatch(reportError(userErrorMessage("Create", "segment", result)));
  }
};

export const getFileOptions = async (store) => {
  const filesAdapter = await getFileManagerAdapter(store);
  let filesConfig = {
    status: "live",
    limit: 25,
    page_order: "file_location_id",
    offset: 0,
    reverse: true,
  };
  const files = await filesAdapter.fetchAllFiles(filesConfig);

  store.dispatch(setFileOptions(files));
};

export const getCTIDOptions = async (store) => {
  const ctidList = await selectClientsAdapter.fetchAllClientCTIDs();
  store.dispatch(setCTIDOptions(ctidList));
};

export const getCommercialCTIDOptions = async (store) => {
  const commercialClientList =
    await selectClientsAdapter.fetchCommercialClientCTIDs();
  store.dispatch(setCommercialCTIDOptions(commercialClientList));
};
